import { Typography } from '@mui/material';
import { map } from 'es-toolkit/compat';
import React from 'react';

import { DownloadFilesModal, FileToDownload } from 'components/atoms/DownloadFilesModal';
import {
  CellTableArtifactFile,
  discoverCellTableArtifactFiles,
} from 'components/StudiesDashboard/StudyExportMenu/cellTableArtifactDiscovery';
import { StartExport } from 'services/export/useSeparateOutputExports';
import { ExportScopeFields, ExportScopeValue } from './ExportFormFields';
import { ExportFormShell } from './ExportFormShell';
import { describeResultSource, getCasesFieldLabel, getScopeOptionLabels } from './exportScopeLabels';
import { ResultsExportContext } from './useResultsExportContext';

interface OriginalFilesExportFormProps {
  context: ResultsExportContext;
  startExport: StartExport;
  isExportRunning: boolean;
  onBack: () => void;
}

const toDownloadFiles = (artifacts: CellTableArtifactFile[]): FileToDownload[] =>
  map(artifacts, (artifact) => ({
    id: artifact.id,
    url: artifact.urlForGetRequest,
    title: artifact.title,
    caseId: artifact.caseId,
    slideId: artifact.slideId,
    orchestrationId: artifact.orchestrationId,
  }));

export const OriginalFilesExportForm: React.FC<OriginalFilesExportFormProps> = ({
  context,
  startExport,
  isExportRunning,
  onBack,
}) => {
  const { study, studyId, isSlidesMode, casesParamsWithoutPaginationAndSorting } = context;
  const [files, setFiles] = React.useState<FileToDownload[] | null>(null);

  const scopeKey = context.hasSpecificSelections
    ? 'selection'
    : context.areFiltersAppliedExceptStudy
    ? 'filtered'
    : 'whole-study';
  const scopeLabels = getScopeOptionLabels(isSlidesMode);
  const scopeLabel = context.hasSpecificSelections
    ? scopeLabels.selected
    : context.areFiltersAppliedExceptStudy
    ? scopeLabels.filtered
    : scopeLabels.whole;

  const discoverFiles = (signal: AbortSignal) =>
    discoverCellTableArtifactFiles(casesParamsWithoutPaginationAndSorting, signal).then(toDownloadFiles);

  const refreshSelectedFiles = async (selectedFiles: FileToDownload[], signal: AbortSignal) => {
    const refreshedFiles = await discoverFiles(signal);
    const refreshedFilesById = new Map(map(refreshedFiles, (file) => [file.id, file]));
    const refreshedSelection = map(selectedFiles, (file) => refreshedFilesById.get(file.id)).filter(
      Boolean
    ) as FileToDownload[];
    if (refreshedSelection.length !== selectedFiles.length) {
      throw new Error('One or more selected cell table files are no longer available.');
    }
    return refreshedSelection;
  };

  const findFiles = (): void => {
    void startExport({
      title: 'Cells table export',
      canCancel: true,
      closeOnSuccess: true,
      initialStage: 'Gathering cell table files...',
      run: async (signal, reportProgress) => {
        const discovered = await discoverCellTableArtifactFiles(
          casesParamsWithoutPaginationAndSorting,
          signal,
          undefined,
          ({ completedPages, inspectedSlides, discoveredFiles }) =>
            reportProgress({
              stage: `${completedPages} pages, ${inspectedSlides} slides, ${discoveredFiles} files discovered`,
            })
        );
        setFiles(toDownloadFiles(discovered));
      },
    });
  };

  return (
    <>
      <ExportFormShell
        study={study}
        scope={
          <ExportScopeFields>
            <ExportScopeValue label={getCasesFieldLabel(isSlidesMode)} note="Follows the current Dataset selection.">
              {scopeLabel}
            </ExportScopeValue>
            <ExportScopeValue label="Results" note="Follows the Dataset.">
              {describeResultSource(context.resultsMode, context.orchestrationId)}
            </ExportScopeValue>
          </ExportScopeFields>
        }
        options={
          <Typography variant="body2" color="text.secondary">
            Original Parquet files, unchanged. After the files are found you choose which ones to download. Several
            selected files can download as one ZIP.
          </Typography>
        }
        primaryLabel="Find files"
        primaryDisabled={isExportRunning}
        onPrimary={findFiles}
        onBack={onBack}
      />
      <DownloadFilesModal
        open={Boolean(files)}
        onClose={() => setFiles(null)}
        filesToDownload={files || []}
        dialogTitle="Download Cells Table Parquets"
        emptyMessage="No cell table files are available for the active selection."
        zipConfiguration={{
          archiveName: `cell_tables_study_${studyId}_${scopeKey}.zip`,
          refreshFiles: refreshSelectedFiles,
        }}
      />
    </>
  );
};
